import { useState } from 'react';
import { X } from 'lucide-react';
import SEO from '@/components/SEO';

const Products = () => {
    const [activeFilter, setActiveFilter] = useState('all');
    const [selectedProduct, setSelectedProduct] = useState(null);

    const products = [
        // ============ ANNEALING ============
        {
            id: 'annealing-furnace',
            name: 'Pit Type Annealing Furnace',
            category: 'Annealing Furnace',
            type: 'annealing',
            image: '/paf2.jpeg',
            description:
                'A vertically loaded, deep-well annealing furnace engineered for annealing, tempering, stress relieving, normalising and spheroidising of long components, coils and bundle stock. Ceramic-fibre insulation with multi-zone PID control delivers tight ±5°C uniformity, while a counter-weighted top cover and forced-air circulation ensure even temperature distribution across the load.',
            specs: [
                { label: 'Capacity Range', value: '250 KGS TO 3 TONS' },
                { label: 'Power Rating', value: '36 KWS TO 150 KWS' },
                { label: 'Power Source', value: 'Electric / Gas' },
                { label: 'Applications', value: 'Annealing, Tempering, Stress Relieving, Normalising, Spheroidizing' },
                { label: 'Insulation', value: 'High-density ceramic fibre' },
                { label: 'Control', value: 'Multi-zone PID with HMI' },
            ],
        },
        {
            id: 'normalizing-furnace',
            name: 'Normalizing Furnace',
            category: 'Normalizing Furnace',
            type: 'normalizing',
            image: '/nf.jpeg',
            description:
                'Heavy-duty normalising furnace built with a stainless-steel inner liner and a fully welded structural shell. Designed for refining grain structure and improving toughness of forgings, castings and bar stock through controlled heating and forced-air cooling profiles. Ideal for automotive, fastener and forging industries that demand consistent metallurgy across high-volume batches.',
            specs: [
                { label: 'Material', value: 'Stainless Steel inner liner' },
                { label: 'Max Temperature', value: 'Up to 1000°C' },
                { label: 'Power Source', value: 'Electric / Gas' },
                { label: 'Cooling', value: 'Programmable forced-air recirculation' },
                { label: 'Application', value: 'Grain refinement of forgings, castings and bar stock' },
                { label: 'Finishing', value: 'Polished SS outer panels' },
            ],
        },
        {
            id: 'muffle-furnace',
            name: 'Muffle Furnace',
            category: 'Muffle & Chamber Furnace',
            type: 'muffle-chamber',
            image: '/cf.jpeg',
            description:
                'A sealed muffle chamber for clean, atmosphere-isolated heat treatment of small batches, tool steels, dies and laboratory samples. The fully enclosed muffle protects the workload from combustion products and ambient contamination, while precise temperature control ensures repeatable metallurgy on every cycle.',
            specs: [
                { label: 'Type', value: 'Sealed Muffle Chamber' },
                { label: 'Max Temperature', value: 'Up to 1200°C' },
                { label: 'Control', value: 'Microprocessor PID / HMI' },
                { label: 'Application', value: 'Clean batch heat treatment, lab samples, tool steels' },
                { label: 'Features', value: 'Atmosphere isolation, uniform heating, compact footprint' },
            ],
        },

        // ============ CONTINUOUS MESH BELT ============
        {
            id: 'continuous-hardening',
            name: 'Continuous Mesh Belt Hardening Furnace',
            category: 'Continuous Mesh Belt Furnace',
            type: 'continuous',
            image: '/continous_mesh_belt_roller_type.jpeg',
            description:
                'A production-grade continuous hardening line with a stainless mesh belt running through a controlled endothermic atmosphere, followed by an integrated quench tank, washer and tempering module. Engineered for high-throughput hardening of fasteners, springs, bearings, chains and small forgings — delivering consistent case depth and hardness with minimal operator intervention.',
            specs: [
                { label: 'Model Range', value: 'ARKO-CMH-50 to ARKO-CMH-500' },
                { label: 'Capacity Range', value: '50 KGS TO 500 KGS/hr' },
                { label: 'Power Rating', value: '50 KWS TO 180 KWS' },
                { label: 'Power Source', value: 'Electric' },
                { label: 'Atmosphere', value: 'Endothermic, N2-Methanol or Inert Gas' },
                { label: 'Applications', value: 'Hardening, Carburizing, Annealing, Tempering, Austempering, Sintering' },
                { label: 'Industries', value: 'Bearings, Chains, Auto Parts, Bicycle Parts, Hand Tools, Springs, Fasteners' },
            ],
        },
        {
            id: 'continuous-tempering',
            name: 'Continuous Mesh Belt Tempering Furnace',
            category: 'Continuous Mesh Belt Furnace',
            type: 'continuous',
            image: '/cmbf2.jpeg',
            description:
                'In-line tempering furnace designed to pair with our continuous mesh belt hardening lines. Features a fully insulated chamber, calibrated belt drive and forced convection to deliver uniform tempering temperatures across the full belt width — ensuring repeatable mechanical properties on every component.',
            specs: [
                { label: 'Model Range', value: 'ARKO-CMT-50 to ARKO-CMT-500' },
                { label: 'Capacity Range', value: '50 KGS TO 500 KGS/hr' },
                { label: 'Power Rating', value: '30 KWS TO 120 KWS' },
                { label: 'Power Source', value: 'Electric' },
                { label: 'Convection', value: 'High-velocity forced air recirculation' },
                { label: 'Industries', value: 'Bearings, Chains, Auto Parts, Hand Tools, Springs, Fasteners, Surgical Tools' },
            ],
        },
        {
            id: 'tray-pusher',
            name: 'Tray Pusher Type Furnace',
            category: 'Tray Pusher Furnace',
            type: 'tray-pusher',
            image: '/tray_pusher.jpeg',
            description:
                'Continuous pusher-type furnace using stackable trays for medium-to-heavy components. Equipped with energy-efficient recuperative burners, tight zone-temperature control and tray transfer system — ideal for hardening, normalising and stress relieving of larger automotive and forging parts at production scale.',
            specs: [
                { label: 'Capacity Range', value: '500 KGS TO 5 TONS' },
                { label: 'Power Rating', value: '30 KWS TO 200 KWS' },
                { label: 'Power Source', value: 'Electric / Gas' },
                { label: 'Burner Type', value: 'Recuperative (energy-efficient)' },
                { label: 'Applications', value: 'Hardening, Normalizing, Annealing, Iso-Thermal Annealing, Stress Relieving' },
                { label: 'Features', value: 'Tray transfer system, substantial operational cost savings' },
            ],
        },

        // ============ RETORT ============
        {
            id: 'rotary-retort',
            name: 'Rotary Retort Furnace',
            category: 'Rotary Retort Furnace',
            type: 'rotary-retort',
            image: '/rrf5.jpeg',
            description:
                'A complete heat-treatment cell built around a sealed rotary retort with controlled protective atmosphere — ideal for bulk hardening, carbo-nitriding and carburising of small components such as bearings, fasteners, pins and bushes. The cell typically includes the rotary retort furnace, pit-type tempering furnace, quenching tank with agitator, charge carrier, heat exchanger and cooling tower.',
            specs: [
                { label: 'Model Range', value: 'TRE-2A to TRE-6' },
                { label: 'Capacity Range', value: '50-100 KGS TO 500 KGS' },
                { label: 'Hardening Power', value: '24 KWS TO 54 KWS' },
                { label: 'Tempering Power', value: '12 KWS TO 27 KWS' },
                { label: 'Power Source', value: 'Electric' },
                { label: 'Atmosphere', value: 'Endothermic / Methanol cracked' },
                { label: 'Cell Components', value: 'Rotary Retort, Pit Tempering, Quench Tank, Geared Agitator, Charge Carrier, Heat Exchanger, Cooling Tower' },
            ],
        },
        {
            id: 'ss-retort',
            name: 'Stainless Steel Retort',
            category: 'Rotary Retort Furnace',
            type: 'rotary-retort',
            image: '/sr.jpeg',
            description:
                'Precision-fabricated stainless-steel retort vessels (SS-310 / SS-330) for protected-atmosphere heat treatment. Vacuum-tested welds and heavy-section construction give long service life under cyclic high-temperature conditions — supplied as new retorts or as direct replacements for retorts in third-party furnaces.',
            specs: [
                { label: 'Material', value: 'SS-310 / SS-330 (heat-resistant alloy)' },
                { label: 'Max Temperature', value: 'Up to 1100°C' },
                { label: 'Features', value: 'Vacuum-tested welds, long service life under cyclic loads' },
                { label: 'Application', value: 'Carburizing, Carbonitriding, Annealing, Controlled-atmosphere heat treatment' },
                { label: 'Customisation', value: 'Custom diameters and lengths to match existing furnaces' },
            ],
        },
        {
            id: 'bogie-hearth',
            name: 'Bogie Hearth Furnace',
            category: 'Bogie Hearth Furnace',
            type: 'bogie-hearth',
            image: '/bhf.jpeg',
            description:
                'A heavy-duty bogie hearth furnace well-suited for mass production of large components. Features motorised bogie movement, PLC-based control of furnace processes, and excellent heat distribution — making it ideal for hardening, annealing, tempering, stress relieving and normalizing of forgings, dies and structural assemblies.',
            specs: [
                { label: 'Capacity Range', value: '500 KGS TO 2 TONS' },
                { label: 'Power Rating', value: '54 KWS TO 150 KWS' },
                { label: 'Power Source', value: 'Electric / Gas' },
                { label: 'Applications', value: 'Hardening, Annealing, Tempering, Stress Relieving, Normalizing' },
                { label: 'Control System', value: 'PLC + HMI based with recipe storage' },
                { label: 'Bogie', value: 'Motorised, rail-guided' },
            ],
        },

        // ============ GAS CARBURIZING ============
        {
            id: 'gas-carburizing',
            name: 'Pit Type Gas Carburizing Furnace',
            category: 'Gas Carburising Furnace',
            type: 'carburizing',
            image: '/gcf.jpeg',
            description:
                'A pit-type carburizing furnace with internal alloy retort, oil/gas-tight sealing and full atmosphere control — designed for deep-case carburizing of gears, shafts, pinions and transmission components. Comes with integrated quench tank, atmosphere generator interface and HMI-based recipe management for repeatable case depths.',
            specs: [
                { label: 'Capacity Range', value: '100 KGS TO 2 TONS' },
                { label: 'Power Rating', value: '36 KWS TO 150 KWS' },
                { label: 'Power Source', value: 'Electric / Gas' },
                { label: 'Type', value: 'Batch-type, sealed pit construction' },
                { label: 'Atmosphere', value: 'Endothermic / Methanol cracked' },
                { label: 'Applications', value: 'Hardening, Carburizing, Carbonitriding, Annealing, Tempering, Normalising' },
                { label: 'Features', value: 'Internal alloy retort, completely sealed, minimal floor space' },
            ],
        },
        {
            id: 'chamber-furnace',
            name: 'Sealed Quench Furnace',
            category: 'Sealed Quench Furnace',
            type: 'sealed-quench',
            image: '/sealed_quench.jpeg',
            description:
                'A fully sealed-atmosphere quench furnace built for carburizing, carbo-nitriding and hardening with direct integrated oil quench. The leak-tight chamber maintains a controlled endothermic atmosphere from heating through quench transfer, ensuring repeatable case-depth, minimal distortion and bright surface finish on gears, shafts and precision components.',
            specs: [
                { label: 'Type', value: 'Sealed Atmosphere Chamber + Integrated Oil Quench' },
                { label: 'Max Temperature', value: 'Up to 950°C' },
                { label: 'Atmosphere', value: 'Endothermic / N2-Methanol' },
                { label: 'Loading', value: 'Front-loading with internal vestibule' },
                { label: 'Applications', value: 'Hardening, Carburizing, Carbo-nitriding, Bright Annealing' },
                { label: 'Features', value: 'Leak-tight construction, oil-tight inner door, programmable cycles' },
            ],
        },

        // ============ SALT BATH ============
        {
            id: 'salt-bath',
            name: 'Salt Bath Furnace',
            category: 'Salt Bath Furnace',
            type: 'salt-bath',
            image: '/srr.jpeg',
            description:
                'Electrically heated salt bath furnace using molten neutral or activated salts as the heating medium. Delivers uniform, rapid and contamination-free heat transfer — ideal for hardening of high-speed steels, dies, taps, drills, springs and small precision components where dimensional stability is critical.',
            specs: [
                { label: 'Type', value: 'External / Internal Electrode' },
                { label: 'Max Temperature', value: 'Up to 1300°C' },
                { label: 'Power Source', value: 'Electric (3-phase)' },
                { label: 'Salt Type', value: 'Neutral / Activated cyanide-free salts' },
                { label: 'Applications', value: 'Hardening, Tempering, Austempering, Martempering, Cleaning' },
                { label: 'Features', value: 'Uniform heat transfer, rapid heating, distortion-free quench' },
            ],
        },

        // ============ SHAKER HEARTH ============
        {
            id: 'shaker-hearth',
            name: 'Shaker Hearth Furnace',
            category: 'Shaker Hearth Furnace',
            type: 'shaker-hearth',
            image: '/shaker_hearth.webp',
            description:
                'Continuous shaker hearth furnace with reciprocating hearth motion that gently advances parts through controlled atmosphere zones. Engineered for hardening and carburizing of small, delicate components like ball bearings, rollers, fasteners and small forgings where mesh-belt marking is unacceptable.',
            specs: [
                { label: 'Capacity Range', value: '50 KGS TO 300 KGS/hr' },
                { label: 'Power Rating', value: '36 KWS TO 100 KWS' },
                { label: 'Power Source', value: 'Electric' },
                { label: 'Atmosphere', value: 'Endothermic / Methanol cracked' },
                { label: 'Applications', value: 'Hardening, Carburizing, Carbo-nitriding of small components' },
                { label: 'Features', value: 'No belt marking, gentle handling, integrated quench tank' },
            ],
        },

        // ============ SPARE PARTS ============
        {
            id: 'electrical-control-panel',
            name: 'Electrical Control Panel',
            category: 'Spare Parts',
            type: 'spares',
            image: '/ep.jpeg',
            description:
                'Plug-and-play industrial control panels with PLC, HMI, recipe management, alarm logging, data logging and safety interlocks — ready to retrofit onto existing furnaces or supplied as part of a new line. Designed for stable long-term operation in industrial environments.',
            specs: [
                { label: 'PLC', value: 'Siemens / Mitsubishi / Allen-Bradley' },
                { label: 'HMI', value: 'Industrial touchscreen with recipe storage' },
                { label: 'Features', value: 'Temperature monitoring, alarm logging, data trends, safety interlocks' },
                { label: 'Application', value: 'Industrial furnace control and automation' },
                { label: 'Build', value: 'Powder-coated steel enclosure, IP54 rated' },
            ],
        },
        {
            id: 'heating-elements',
            name: 'Heating Elements (Kanthal)',
            category: 'Spare Parts',
            type: 'spares',
            image: '/ch1.jpeg',
            description:
                'Kanthal-grade FeCrAl heating elements wound and shaped for our own furnaces as well as most third-party makes. Stable resistance characteristics, long element life and predictable performance at temperatures up to 1400°C make these the preferred choice for production environments.',
            specs: [
                { label: 'Material', value: 'Kanthal A1 / APM (FeCrAl)' },
                { label: 'Form', value: 'Strips or Coils — custom-wound to drawing' },
                { label: 'Max Temperature', value: 'Up to 1400°C' },
                { label: 'Features', value: 'Long service life, stable resistance, quick-replace fittings' },
            ],
        },
        {
            id: 'radiant-tube',
            name: 'Radiant Tube Heaters',
            category: 'Spare Parts',
            type: 'spares',
            image: '/rth.jpeg',
            description:
                'Self-recuperative and conventional radiant tubes for indirect, atmosphere-protected heating. Engineered for long life under thermal cycling, with SS / Inconel material options and field-replaceable burner assemblies.',
            specs: [
                { label: 'Type', value: 'Self-recuperative or conventional radiant tubes' },
                { label: 'Material', value: 'SS / Inconel' },
                { label: 'Max Temperature', value: 'Up to 1100°C' },
                { label: 'Features', value: 'Uniform indirect heating, long life, field-replaceable' },
            ],
        },
        {
            id: 'bundle-rod',
            name: 'Bundle Rod Heater',
            category: 'Spare Parts',
            type: 'spares',
            image: '/brh.jpeg',
            description:
                'Industrial bundle-rod heaters for efficient direct immersion heating of process liquids, salt baths and quench media. Available in copper and aluminium with a range of wattages and sheath options.',
            specs: [
                { label: 'Sheath Material', value: 'Aluminium / Copper / Incoloy' },
                { label: 'Power Source', value: 'Electric' },
                { label: 'Shape', value: 'Round bundle' },
                { label: 'Application', value: 'Industrial liquid / bath heating' },
            ],
        },
        {
            id: 'immersion-heater',
            name: 'Immersion Heater',
            category: 'Spare Parts',
            type: 'spares',
            image: '/ih.jpeg',
            description:
                'Polished-steel immersion heaters for industrial liquid heating — quench oils, salt baths and washer tanks. Robust flange or screw-in mountings with replaceable elements for long service life.',
            specs: [
                { label: 'Material', value: 'Polished Steel sheath' },
                { label: 'Power Source', value: 'Electric' },
                { label: 'Shape', value: 'Round / U-bend' },
                { label: 'Mounting', value: 'Flange / Screw-in' },
            ],
        },
        {
            id: 'tubular-heater',
            name: 'Tubular Heater',
            category: 'Spare Parts',
            type: 'spares',
            image: '/th.jpeg',
            description:
                'Polished stainless-steel tubular heaters for industrial applications — supplied straight, bent or formed to drawing. Long-life MgO insulation with welded end seals for moisture-tight operation.',
            specs: [
                { label: 'Material', value: 'Stainless Steel (304/316)' },
                { label: 'Finishing', value: 'Polished' },
                { label: 'Power Source', value: 'Electric' },
                { label: 'Application', value: 'General industrial heating, ovens and dryers' },
            ],
        },
        {
            id: 'quenching-tank',
            name: 'Quenching Tank',
            category: 'Spare Parts',
            type: 'spares',
            image: '/qt.jpeg',
            description:
                'Heavy-gauge welded quenching tank with submerged agitation, heat-exchanger interface, elevator/platform options and oil-skimming arrangement. Tailored to your line flow, quench medium (oil / polymer / water) and load size — built for decades of cyclic service.',
            specs: [
                { label: 'Material', value: 'Mild Steel / Stainless Steel' },
                { label: 'Quench Medium', value: 'Oil / Polymer / Water' },
                { label: 'Agitation', value: 'Submerged propeller, geared motor' },
                { label: 'Frequency', value: '50 Hz' },
                { label: 'Application', value: 'Rapid cooling in heat-treatment cells' },
                { label: 'Country of Origin', value: 'India' },
            ],
        },
    ];

    const filters = [
        { id: 'all', label: 'All Products' },
        { id: 'carburizing', label: 'Gas Carburising Furnace' },
        { id: 'continuous', label: 'Continuous Mesh Belt Furnace' },
        { id: 'tray-pusher', label: 'Tray Pusher Furnace' },
        { id: 'muffle-chamber', label: 'Muffle & Chamber Furnace' },
        { id: 'rotary-retort', label: 'Rotary Retort Furnace' },
        { id: 'bogie-hearth', label: 'Bogie Hearth Furnace' },
        { id: 'salt-bath', label: 'Salt Bath Furnace' },
        { id: 'shaker-hearth', label: 'Shaker Hearth Furnace' },
        { id: 'normalizing', label: 'Normalizing Furnace' },
        { id: 'annealing', label: 'Annealing Furnace' },
        { id: 'spares', label: 'Spare Parts' },
    ];

    const filteredProducts =
        activeFilter === 'all' ? products : products.filter((p) => p.type === activeFilter);

    return (
        <div className="min-h-screen bg-gray-50">
            <SEO
                path="/products"
                title="Industrial Furnaces & Heat Treatment Products — Arkotherm Furnaces"
                description="Browse Arkotherm Furnaces' full range: Continuous Mesh Belt Hardening, Rotary Retort, Gas Carburizing, Annealing, Normalizing, Muffle and Salt Bath furnaces — plus spare parts. ISO 9001 Certified."
                image="https://www.arkothermfurnaces.com/rrf5.jpeg"
            />
            {/* Hero */}
            <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <div className="inline-block px-4 py-2 bg-orange-500/20 border border-orange-500 rounded-full text-sm font-semibold mb-6">
                            Our Products · ISO 9001 Certified
                        </div>
                        <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                            Industrial Furnaces <span className="gradient-text">&amp;</span><br />
                            <span className="gradient-text">Heat Treatment Solutions</span>
                        </h1>
                        <p className="text-xl text-gray-300 leading-relaxed">
                            Explore our comprehensive range of industrial furnaces and heat treatment
                            equipment — designed, manufactured and commissioned in-house at our Amritsar
                            and Ludhiana facilities.
                        </p>
                    </div>
                </div>
            </section>

            {/* Filters */}
            <section className="bg-white border-b sticky top-[72px] z-40 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Filters — dropdown on mobile, wrapped pills on desktop */}
            <div className="sm:hidden">
                <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wider">
                    Browse by Category
                </label>
                <div className="relative">
                    <select
                        value={activeFilter}
                        onChange={(e) => setActiveFilter(e.target.value)}
                        data-testid="product-filter-select"
                        className="w-full appearance-none px-4 py-3.5 pr-10 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-bold text-sm shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-300"
                    >
                        {filters.map((filter) => (
                            <option key={filter.id} value={filter.id} className="bg-white text-gray-900">
                                {filter.label}
                            </option>
                        ))}
                    </select>
                    <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-white">
                        ▼
                    </span>
                </div>
            </div>

            <div className="hidden sm:flex flex-wrap gap-3" data-testid="product-filter-tabs">
                {filters.map((filter) => (
                    <button
                        key={filter.id}
                        onClick={() => setActiveFilter(filter.id)}
                        className={`px-6 py-3 rounded-full font-semibold text-sm whitespace-nowrap transition-all duration-300 ${
                            activeFilter === filter.id
                                ? 'gradient-bg text-white shadow-lg scale-105'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
                        }`}
                        data-testid={`filter-${filter.id}`}
                    >
                        {filter.label}
                    </button>
                ))}
            </div>
                </div>
            </section>

            {/* Products Grid */}
            <section className="section-padding">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid={`product-grid-${activeFilter}`}>
                        {filteredProducts.map((product) => (
                            <div
                                key={product.id}
                                className="bg-white rounded-2xl overflow-hidden shadow-md hover-lift cursor-pointer border border-gray-100"
                                onClick={() => setSelectedProduct(product)}
                                data-testid={`product-${product.id}`}
                            >
                                <div className="relative overflow-hidden h-56">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                                    />
                                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                                        <span className="text-sm font-semibold text-orange-600">{product.category}</span>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">{product.name}</h3>
                                    <p className="text-gray-600 mb-4 line-clamp-3">{product.description}</p>
                                    <button className="w-full px-6 py-3 gradient-bg text-white font-semibold rounded-full hover:shadow-xl transition-all duration-300">
                                        View Specifications
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {filteredProducts.length === 0 && (
                        <div className="text-center py-20 text-gray-500">
                            No products in this category yet — please get in touch for a custom solution.
                        </div>
                    )}
                </div>
            </section>

            {/* CTA */}
            <section className="bg-gradient-to-r from-orange-500 to-yellow-500 py-16">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <h2 className="text-4xl font-bold text-white mb-4">Need a Custom Solution?</h2>
                    <p className="text-xl text-white/90 mb-8">
                        Tell us your requirements and we&apos;ll design a solution tailored to your needs.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <a
                            href="/contact"
                            className="inline-block px-8 py-4 bg-white text-orange-600 font-bold rounded-full hover:shadow-2xl transition-all duration-300"
                        >
                            Request Quote
                        </a>
                        <a
                            href="/catalog.pdf"
                            download="catalog.pdf"
                            className="inline-block px-8 py-4 bg-white/20 backdrop-blur-sm border-2 border-white text-white font-bold rounded-full hover:bg-white/30 transition-all duration-300"
                            data-testid="products-download-catalog-btn"
                        >
                            Download Catalog
                        </a>
                    </div>
                </div>
            </section>

            {/* Product Modal */}
            {selectedProduct && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
                    <div className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-fadeIn">
                        {/* Modal Header */}
                        <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-yellow-500 px-8 py-6 flex justify-between items-center">
                            <div>
                                <h2 className="text-3xl font-bold text-white">{selectedProduct.name}</h2>
                                <p className="text-white/90 mt-1">{selectedProduct.category}</p>
                            </div>
                            <button
                                onClick={() => setSelectedProduct(null)}
                                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                                data-testid="modal-close-btn"
                            >
                                <X size={28} className="text-white" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="p-8">
                            {/* Image */}
                            <div className="mb-8">
                                <img
                                    src={selectedProduct.image}
                                    alt={selectedProduct.name}
                                    className="w-full h-80 object-cover rounded-2xl shadow-lg"
                                />
                            </div>

                            {/* Description */}
                            <div className="mb-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <span className="w-1 h-8 bg-gradient-to-b from-orange-500 to-yellow-500 rounded-full"></span>
                                    Product Description
                                </h3>
                                <p className="text-gray-700 leading-relaxed text-lg">{selectedProduct.description}</p>
                            </div>

                            {/* Specifications Table */}
                            <div className="mb-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                                    <span className="w-1 h-8 bg-gradient-to-b from-orange-500 to-yellow-500 rounded-full"></span>
                                    Technical Specifications
                                </h3>
                                <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                                    <table className="w-full">
                                        <tbody className="divide-y divide-gray-200">
                                            {selectedProduct.specs.map((spec, index) => (
                                                <tr
                                                    key={index}
                                                    className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                                                >
                                                    <td className="py-4 px-6 font-semibold text-gray-900 w-1/3">
                                                        {spec.label}
                                                    </td>
                                                    <td className="py-4 px-6 text-gray-700">
                                                        {spec.value}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* CTA Button */}
                            <div className="flex gap-4">
                                <a
                                    href="/contact"
                                    className="flex-1 px-8 py-4 gradient-bg text-white font-bold rounded-full hover:shadow-2xl transition-all text-center text-lg"
                                >
                                    Request Quote
                                </a>
                                <a
                                    href="/contact"
                                    className="flex-1 px-8 py-4 bg-gray-100 text-gray-900 font-bold rounded-full hover:bg-gray-200 transition-all text-center text-lg"
                                >
                                    Enquire Now
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Products;
